import { useState, useEffect, useRef, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ReadinessChecklist } from "@/components/ota/OtaReadinessChecklist";
import { OtaStatePhases } from "@/components/ota/OtaStatePhases";
import { OtaPrivilegeException, exceptionUsable } from "@/components/ota/OtaPrivilegeException";
import {
  X, RefreshCw, ArrowUpCircle, RotateCcw, CheckCircle2, XCircle, Loader2,
  ShieldAlert, CircleDot, Clock, GitCommitHorizontal,
} from "lucide-react";

// Étapes canoniques affichées (backend + device).
const STEP_LABELS = {
  PRECHECK: "Préconditions", RELEASE_RESOLVE: "Résolution release", VERIFY: "Vérif manifeste",
  LOCK: "Verrou drone", READINESS: "Préflight serveur", DISPATCH: "Envoi Infra API", FETCH: "Fetch SHA",
  VERIFY_MANIFEST: "Vérif manifeste (device)", VERIFY_STATE: "Vérif état device", BACKUP: "Sauvegarde", BACKUP_VERIFY: "Vérif sauvegarde",
  INSTALL: "Installation", COMPILE: "Compilation", RESTART: "Redémarrage", HEALTH: "Health check",
  WRITE_RELEASE: ".skycore-release",
};
const STEP_ICON = (s) => {
  if (s === "success") return <CheckCircle2 size={13} className="text-[#22C55E]" />;
  if (s === "failed") return <XCircle size={13} className="text-[#EF4444]" />;
  if (s === "running") return <Loader2 size={13} className="text-[#3B82F6] animate-spin" />;
  if (s === "skipped") return <CircleDot size={12} className="text-[#525252]" />;
  return <Clock size={12} className="text-[#525252]" />;
};
const BACKUP_LABEL = {
  MISSING: "absente", INCONSISTENT: "corrompue ou incohérente", UNKNOWN: "inconnue", UNREACHABLE: "injoignable",
};
const TERMINAL = ["success", "failed", "rolled_back", "rollback_failed", "result_unknown"];
const JOB_BADGE = {
  success: { c: "#22C55E", l: "Succès" }, failed: { c: "#EF4444", l: "Échec" },
  rolled_back: { c: "#F59E0B", l: "Rollback effectué" }, rollback_failed: { c: "#EF4444", l: "Rollback échoué" },
  result_unknown: { c: "#A855F7", l: "Résultat incertain" },
};

export default function OtaAgentModal({ drone, onClose }) {
  const [dep, setDep] = useState(null);
  const [rd, setRd] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);
  const pollRef = useRef(null);

  const rid = drone.id;

  const loadDeployment = useCallback(() => {
    api.get(`/ota/drones/${rid}/deployment`).then((r) => setDep(r.data)).catch((e) => { toast.error(apiErr(e)); setDep({ error: true }); });
    setRd(null);
    api.get(`/ota/drones/${rid}/readiness`).then((r) => setRd(r.data)).catch(() => setRd({ error: true }));
  }, [rid]);

  useEffect(() => { loadDeployment(); }, [loadDeployment]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  // Poll d'un job actif jusqu'à état terminal.
  const pollJob = useCallback((jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/ota/jobs/${jobId}`);
        setJob(data);
        if (TERMINAL.includes(data.status)) {
          clearInterval(pollRef.current);
          loadDeployment();
          const b = JOB_BADGE[data.status];
          (data.status === "success" ? toast.success : toast.error)(`Déploiement : ${b?.l || data.status}`);
        }
      } catch (e) { /* transient */ }
    }, 1500);
  }, [loadDeployment]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  // Reprise d'un job actif renvoyé par le backend (dep.active_job) : adoption + polling.
  // Évite double polling via un garde sur l'id déjà adopté.
  const adoptedRef = useRef(null);
  useEffect(() => {
    const aj = dep?.active_job;
    if (aj && aj.id && !TERMINAL.includes(aj.status) && adoptedRef.current !== aj.id) {
      adoptedRef.current = aj.id;
      setJob(aj);
      pollJob(aj.id);
    }
  }, [dep, pollJob]);

  const startUpdate = async (release) => {
    setBusy("update");
    try {
      const key = `${rid}-${release}-${Date.now()}`;
      const { data } = await api.post(`/ota/drones/${rid}/update-agent`, { release }, { headers: { "X-OTA-Key": key } });
      toast.success("Job de mise à jour créé");
      setJob({ id: data.job_id, status: data.status, steps: [] });
      pollJob(data.job_id);
    } catch (e) { toast.error(apiErr(e)); loadDeployment(); } finally { setBusy(""); }
  };

  const startRollback = async () => {
    setBusy("rollback");
    try {
      const { data } = await api.post(`/ota/drones/${rid}/rollback-agent`, {}, { headers: { "X-OTA-Key": `${rid}-rb-${Date.now()}` } });
      toast.success("Job de rollback créé");
      setJob({ id: data.job_id, status: data.status, steps: [] });
      pollJob(data.job_id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const available = dep?.available;
  const installedRelease = dep?.installed_release;
  const updateAvailable = dep?.update_available;
  const expired = !rd?.expires_at || now > new Date(rd.expires_at).getTime();
  // deploy_allowed reste false avec l'exception : aucun GO global artificiel, autorisation distincte.
  const viaException = rd?.deploy_allowed !== true && exceptionUsable(rd, now);
  const canDeploy = (rd?.deploy_allowed === true || viaException) && !expired && rd?.target_release === available?.release;
  const canRollback = rd?.rollback_allowed === true && !expired;
  const rbStatus = rd?.rollback_status;
  const jobActive = job && !TERMINAL.includes(job.status);
  const disabled = !canDeploy || jobActive || busy;

  return (
    <div className="fixed inset-0 z-[1900] flex items-center justify-center bg-black/70 p-4" data-testid="ota-modal">
      <div className="bg-[#0F0F0F] border border-white/10 rounded-md w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0F0F0F]">
          <div className="flex items-center gap-2">
            <ArrowUpCircle size={16} className="text-[#3B82F6]" />
            <span className="font-mono text-[12px] uppercase tracking-wider">Agent SkyCore — Déploiement OTA · {drone.drone_id}</span>
          </div>
          <button data-testid="ota-close" onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Versions installées / disponibles */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#141414] border border-white/10 rounded-sm p-3" data-testid="ota-installed">
              <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-1">Release installée</div>
              <div className="font-mono text-[12px] text-white">{installedRelease || (dep?.infra_not_deployed ? "—" : "inconnue")}</div>
              {dep?.installed?.git_sha && <div className="font-mono text-[9px] text-[#737373] flex items-center gap-1 mt-1"><GitCommitHorizontal size={10} />{dep.installed.git_sha.slice(0, 12)}</div>}
            </div>
            <div className="bg-[#141414] border border-white/10 rounded-sm p-3" data-testid="ota-available">
              <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-1">Release disponible</div>
              <div className="font-mono text-[12px] text-white">{available?.release || "—"}</div>
              {available?.git_sha && <div className="font-mono text-[9px] text-[#737373] flex items-center gap-1 mt-1"><GitCommitHorizontal size={10} />{available.git_sha.slice(0, 12)}</div>}
              <div className="text-[9px] text-[#737373] mt-1">Fichiers : {(available?.files || []).join(", ") || "—"}</div>
            </div>
          </div>

          {dep?.infra_not_deployed && (
            <div data-testid="ota-infra-not-deployed" className="bg-[#A855F7]/10 border border-[#A855F7]/30 rounded-sm px-3 py-2 text-[11px] text-[#C4B5FD]">
              Infra API OTA non déployée : les routes <code>deploy-agent</code> / <code>deployment</code> seront disponibles après redéploiement Hetzner (voir OTA_DEPLOY_SETUP.md).
            </div>
          )}

          <div data-testid="ota-update-state" className="text-[11px]">
            {updateAvailable
              ? <span className="text-[#F59E0B]">● Release plus récente disponible{viaException && canDeploy ? " — autorisée par exception opérateur (sudo NO-GO maintenu)" : (canDeploy ? "" : " — installation non autorisée (préflight NO-GO)")}</span>
              : (installedRelease ? <span className="text-[#22C55E]">● À jour</span> : <span className="text-[#737373]">● État indéterminé</span>)}
          </div>

          {/* Préflight guidé (AGENT.OTA.READINESS.1) */}
          <div className="bg-[#141414] border border-white/10 rounded-sm p-3 space-y-3" data-testid="ota-precheck">
            <div className="text-[9px] uppercase text-[#737373] tracking-wider flex items-center gap-1.5"><ShieldAlert size={12} /> Préflight de sécurité</div>
            <OtaStatePhases readiness={rd} expired={expired} />
            <ReadinessChecklist readiness={rd} expired={expired} />
          </div>

          <OtaPrivilegeException rid={rid} readiness={rd} now={now} onChange={loadDeployment} />

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button data-testid="ota-check" onClick={loadDeployment} disabled={!!busy}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">
              <RefreshCw size={12} /> Vérifier
            </button>
            <button data-testid="ota-update" disabled={disabled || !available}
              onClick={() => setConfirm({
                kind: "update",
                title: `Mettre à jour l'agent — ${drone.drone_id}`,
                message: `${viaException ? `EXCEPTION OPÉRATEUR : privileges_minimal reste NO-GO ; l'exception ${rd.privilege_exception.current.id} sera consommée (usage unique). ` : ""}Déployer la release ${available?.release} (${(available?.files || []).length} fichiers). La checklist sera recalculée par le serveur à l'envoi ; sauvegarde vérifiée avant installation, VERIFY_STATE local, rollback automatique en cas d'échec.`,
                onYes: () => startUpdate(available.release),
              })}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-3 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40 disabled:cursor-not-allowed">
              <ArrowUpCircle size={12} /> Mettre à jour
            </button>
            <button data-testid="ota-rollback" disabled={jobActive || busy || !canRollback}
              title={canRollback ? "" : `Rollback indisponible : sauvegarde ${BACKUP_LABEL[rbStatus?.previous_known_good?.status] || "inconnue"}`}
              onClick={() => setConfirm({
                kind: "rollback", critical: true,
                title: `Rollback de l'agent — ${drone.drone_id}`,
                message: "Restaurer la dernière release fonctionnelle connue (previous_known_good). Backup + compile + restart + health.",
                onYes: startRollback,
              })}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-3 py-2 hover:bg-[#F59E0B]/10 disabled:opacity-40">
              <RotateCcw size={12} /> Rollback
            </button>
            <span data-testid="ota-rollback-state" className="self-center font-mono text-[9px] text-[#A3A3A3]">
              Sauvegarde précédente : {rbStatus?.previous_known_good?.status === "OK"
                ? `${rbStatus.previous_known_good.release} vérifiée`
                : (BACKUP_LABEL[rbStatus?.previous_known_good?.status] || "inconnue")}
            </span>
          </div>

          {/* Progression du job */}
          {job && (
            <div className="bg-[#141414] border border-white/10 rounded-sm p-3" data-testid="ota-job">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] text-[#A3A3A3]">Job {job.id}</span>
                {TERMINAL.includes(job.status)
                  ? <span data-testid="ota-job-status" className="font-mono text-[10px] uppercase font-bold" style={{ color: JOB_BADGE[job.status]?.c }}>{JOB_BADGE[job.status]?.l || job.status}</span>
                  : <span data-testid="ota-job-status" className="font-mono text-[10px] uppercase text-[#3B82F6] flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> {job.status}</span>}
              </div>
              <div className="space-y-1">
                {(job.steps || []).filter((s) => s.status !== "pending" || !TERMINAL.includes(job.status)).map((s) => (
                  <div key={s.name} className="flex items-center gap-2 text-[10px]" data-testid={`ota-step-${s.name}`}>
                    {STEP_ICON(s.status)}
                    <span className="text-[#D4D4D4] w-40">{STEP_LABELS[s.name] || s.name}</span>
                    {s.message && <span className="text-[#737373] truncate flex-1">{s.message}</span>}
                  </div>
                ))}
              </div>
              {job.error && <div className="mt-2 text-[10px] text-[#FCA5A5]" data-testid="ota-job-error">{job.error.message || job.error.code}{job.error.reasons ? ` — ${job.error.reasons.join(", ")}` : ""}</div>}
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" data-testid="ota-confirm">
          <div className="bg-[#0F0F0F] border border-white/15 rounded-md w-full max-w-md p-5">
            <div className="font-mono text-[12px] uppercase tracking-wider mb-2">{confirm.title}</div>
            <div className="text-[11px] text-[#A3A3A3] mb-4">{confirm.message}</div>
            <div className="flex justify-end gap-2">
              <button data-testid="ota-confirm-cancel" onClick={() => setConfirm(null)} className="font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">Annuler</button>
              <button data-testid="ota-confirm-yes" onClick={() => { const fn = confirm.onYes; setConfirm(null); fn?.(); }}
                className={`font-mono text-[10px] uppercase tracking-wider rounded-sm px-4 py-2 ${confirm.critical ? "bg-[#F59E0B] hover:bg-[#F59E0B]/85 text-black" : "bg-[#3B82F6] hover:bg-[#3B82F6]/85"}`}>Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
